/**
 * useMintConfig.js
 *
 * Reads the announced schedule / site copy from Supabase's `mint_config`
 * table (see supabase/mint_config.sql).
 *
 * ── Precedence, and why it matters ──────────────────────────────────
 * The CONTRACT is authoritative for mint timing. Always. Its
 * `gtdStartTime` is what actually gates `gtdMint()`, checked on-chain,
 * and no database row can override it — someone calling the contract
 * directly from Remix never touches this table at all.
 *
 * So this config is used for exactly one thing: filling the gap BEFORE
 * a time is committed on-chain, so you can announce a date without a
 * redeploy. The moment the contract has a real `gtdStartTime`, that
 * wins and the announced value is ignored for timing.
 *
 * If the two disagree once both are set, that is a real operational
 * problem — the site would be advertising a time the contract will not
 * honour — so `mismatch` is surfaced for the UI to warn about rather
 * than quietly papering over it.
 *
 * Failure here is non-fatal by design: if Supabase is unreachable the
 * mint page still works entirely from chain data.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

/** Tolerance before we call two timestamps a mismatch. */
const MISMATCH_TOLERANCE_SEC = 60;

export default function useMintConfig(onChainGtdStart, onChainPublicStart) {
  const [config, setConfig] = useState({
    loading: true,
    announcedGtdStart: null,    // unix seconds, or null
    announcedPublicStart: null,
    announcement: null,
    mintPageEnabled: true,
    pausedMessage: null,
    mintPageUnlockAt: null,   // unix seconds, or null = no gate
  });

  const [nowTick, setNowTick] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNowTick(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('mint_config')
          .select('announced_gtd_start, announced_public_start, announcement, mint_page_enabled, paused_message, mint_page_unlock_at')
          .eq('id', 1)
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;

        const toUnix = (v) => (v ? Math.floor(new Date(v).getTime() / 1000) : null);

        setConfig({
          loading: false,
          announcedGtdStart: toUnix(data?.announced_gtd_start),
          announcedPublicStart: toUnix(data?.announced_public_start),
          announcement: data?.announcement || null,
          // Default to showing the page: a missing row or a failed read
          // should never hide the mint from users.
          mintPageEnabled: data?.mint_page_enabled !== false,
          pausedMessage: data?.paused_message || null,
          mintPageUnlockAt: toUnix(data?.mint_page_unlock_at),
        });
      } catch (err) {
        console.warn('[mint_config] falling back to on-chain values only:', err.message);
        if (!cancelled) setConfig((c) => ({ ...c, loading: false }));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // On-chain wins whenever it is set (non-zero).
  const effectiveGtdStart = onChainGtdStart || config.announcedGtdStart || 0;
  const effectivePublicStart = onChainPublicStart || config.announcedPublicStart || 0;

  // Both set and materially different → the site would be lying to users.
  const mismatch = Boolean(
    onChainGtdStart &&
    config.announcedGtdStart &&
    Math.abs(onChainGtdStart - config.announcedGtdStart) > MISMATCH_TOLERANCE_SEC
  );

  // Page gate: before `mint_page_unlock_at` the site shows a countdown
  // instead of the mint UI. A curtain over the front door — it changes
  // what your site renders, never what the contract accepts. Someone
  // calling gtdMint() directly is unaffected, which is exactly why the
  // contract does its own on-chain time check.
  //
  // `nowTick` drives re-evaluation. Without it this was only recomputed
  // when the parent happened to re-render (the ~12s contract poll), so
  // the gate could sit there for several seconds after its own countdown
  // had already hit zero.
  // ── Local testing bypass ────────────────────────────────────────
  // Lets you work on the mint page locally while the live site stays
  // gated, WITHOUT touching Supabase (a row change there would unlock
  // the page for real visitors too).
  //
  //   npm start                       -> bypassed automatically
  //   REACT_APP_BYPASS_MINT_GATE=true -> bypassed in a local build too
  //
  // NODE_ENV is set to 'production' by `npm run build` and cannot be
  // overridden by CRA, so the first path can never reach your deployed
  // site. The env-var path CAN, if you set it and then deploy that
  // build — so only put it in a local .env.development, never .env.
  const bypassGate =
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_BYPASS_MINT_GATE === 'true';

  const pageLocked =
    !bypassGate && Boolean(config.mintPageUnlockAt && nowTick < config.mintPageUnlockAt);

  return {
    ...config,
    pageLocked,
    gateBypassed: bypassGate && Boolean(config.mintPageUnlockAt && nowTick < config.mintPageUnlockAt),
    effectiveGtdStart,
    effectivePublicStart,
    /** True when the countdown is showing an announced date, not a live on-chain one. */
    isAnnouncedOnly: !onChainGtdStart && Boolean(config.announcedGtdStart),
    mismatch,
  };
}
